import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que la pantalla de marcas y modelos (M-7) le pide a la API, con la forma de
// `sucursales/datos.ts`: una clave de caché con el taller adentro, una consulta
// por lectura y una función por escritura que devuelve el `ProblemDetails` o
// nada.
//
// Las listas no se paginan: la API las devuelve enteras porque son decenas, y la
// pantalla necesita la de marcas completa para avisar antes de repetir una.

export type Marca = components['schemas']['AssetBrandListItem'];
export type Modelo = components['schemas']['AssetModelListItem'];
export type TipoDeActivo = components['schemas']['AssetTypeListItem'];

export interface FiltrosMarcas {
  buscar: string;
  /** Un id de tipo, o `todos`. Con un tipo, trae las de ese tipo y las genéricas. */
  tipo: string;
  estado: 'todas' | 'activas' | 'inactivas';
}

/// El taller de la pestaña (#205), en toda clave de caché: regla 5 de multitenancy, en
/// el navegador. Igual que en sucursales.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['marcas', taller] as const,
  marcas: (taller: string, filtros: FiltrosMarcas) => ['marcas', taller, 'lista', filtros] as const,
  modelos: (taller: string, marca: string) => ['marcas', taller, 'modelos', marca] as const,
  tipos: (taller: string) => ['tipos-de-activo', taller] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

export function useTiposDeActivo() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.tipos(taller),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/asset-types');
      if (error || !data) throw new Error('No se pudieron leer los tipos de activo');
      return data.items;
    },
  });
}

export function useMarcas(filtros: FiltrosMarcas) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.marcas(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/asset-brands', {
        params: {
          query: {
            ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
            ...(filtros.tipo !== 'todos' && { assetTypeId: filtros.tipo }),
            ...(filtros.estado !== 'todas' && { isActive: filtros.estado === 'activas' }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer la lista de marcas');
      return data.items;
    },
    // Mientras llega el resultado de lo que se escribe en el buscador se sigue
    // viendo el anterior: sin esto la lista parpadea en cada tecla.
    placeholderData: keepPreviousData,
  });
}

/**
 * Todas las marcas, sin filtro. Es lo que mira la ventana de alta para avisar
 * que ya hay una «Toyota» de otro tipo: el filtro de la columna podría estar
 * escondiéndola.
 */
export function useTodasLasMarcas(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.marcas(taller, { buscar: '', tipo: 'todos', estado: 'todas' }),
    enabled: habilitado,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/asset-brands');
      if (error || !data) throw new Error('No se pudo leer la lista de marcas');
      return data.items;
    },
  });
}

export function useModelos(marca: string | null) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.modelos(taller, marca ?? ''),
    enabled: marca !== null,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/asset-brands/{brandId}/models', {
        params: { path: { brandId: marca ?? '' } },
      });
      if (error || !data) throw new Error('No se pudieron leer los modelos');
      return data.items;
    },
  });
}

/**
 * Las escrituras. Al terminar se invalida todo lo de marcas del taller: el
 * conteo de modelos de la marca cambia con cada alta o cambio de estado de un
 * modelo.
 *
 * **También cuando la API dice que no.** Un `conflict.cambio_simultaneo` quiere
 * decir que la versión en caché ya es vieja; sin refrescarla, volver a abrir la
 * ventana mandaría la misma y chocaría otra vez, sin salida.
 */
export function useCambiosMarcas() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown }>(peticion: Promise<T>): Promise<ProblemaApi | undefined> {
    const { error } = await peticion;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return error ? (error as ProblemaApi) : undefined;
  }

  return {
    /**
     * Sin tipo, la marca es genérica: sirve para cualquier tipo de activo.
     * `alCrear` recibe el id que devolvió la API, para dejarla elegida.
     */
    crearMarca: async (nombre: string, tipo: string | null, alCrear?: (id: string) => void) => {
      const { data, error } = await api.POST('/api/asset-brands', {
        // Genérica: sin `assetTypeId`. El contrato no admite `null` en un id (ver
        // `TypedIdSchemaTransformer`); omitirlo es lo mismo para la API.
        body: { name: nombre.trim(), ...(tipo && { assetTypeId: tipo }) },
      });
      if (data) alCrear?.(String(data.id));
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return error ? (error as ProblemaApi) : undefined;
    },

    renombrarMarca: (id: string, versionActual: number, nombre: string) =>
      tras(
        api.PUT('/api/asset-brands/{id}', {
          params: { path: { id } },
          body: { version: versionActual, name: nombre.trim() },
        }),
      ),

    cambiarEstadoMarca: (id: string, activa: boolean) =>
      tras(api.PUT('/api/asset-brands/{id}/status', { params: { path: { id } }, body: { isActive: activa } })),

    /** `alCrear` recibe el id del modelo nuevo, para dejarlo elegido en el formulario del activo. */
    crearModelo: async (marca: string, nombre: string, alCrear?: (id: string) => void) => {
      const { data, error } = await api.POST('/api/asset-brands/{brandId}/models', {
        params: { path: { brandId: marca } },
        body: { name: nombre.trim() },
      });
      if (data) alCrear?.(String(data.id));
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return error ? (error as ProblemaApi) : undefined;
    },

    renombrarModelo: (id: string, versionActual: number, nombre: string) =>
      tras(
        api.PUT('/api/asset-models/{id}', {
          params: { path: { id } },
          body: { version: versionActual, name: nombre.trim() },
        }),
      ),

    cambiarEstadoModelo: (id: string, activo: boolean) =>
      tras(api.PUT('/api/asset-models/{id}/status', { params: { path: { id } }, body: { isActive: activo } })),
  };
}

/**
 * Cómo compara la base dos nombres: sin tildes, sin mayúsculas y sin espacios de
 * sobra (`lower(immutable_unaccent(btrim(name)))`). La pantalla lo usa solo para
 * avisar; la regla la pone la API.
 */
export const mismoNombre = (a: string, b: string) => clave(a) === clave(b);

const clave = (texto: string) =>
  texto
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
